/* eslint-disable consistent-return */
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import * as React from 'react';
import { Editor, Element, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { SlatePlugin } from '../../../../slatePlugins/slatePlugins.types';
import { canDeleteNode } from '../shared-transforms';
import { QuoteElementTransformActions } from './model/transforms';
import {
  ELEMENT_QUOTE,
  ELEMENT_QUOTE_AUTHOR,
  ELEMENT_QUOTE_CONTENT,
  QuotePluginOptions,
} from './QuotePlugin.types';
import { QuoteReadOnlyPlugin } from './QuoteReadOnlyPlugin';

const QuoteElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "QuoteElementEditable" */ './Element/QuoteElement').then(
      (mod) => mod.QuoteElementEditable
    )
  )
);
const QuoteElementAuthorEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "QuoteElementAuthorEditable" */ './Element/QuoteElementAuthor').then(
      (mod) => mod.QuoteElementAuthorEditable
    )
  )
);
const QuoteElementContentEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(
      /* webpackExports: "QuoteElementContentEditable" */ './Element/QuoteElementContent'
    ).then((mod) => mod.QuoteElementContentEditable)
  )
);

export const QuotePlugin = (options: QuotePluginOptions = {}): SlatePlugin => {
  if (options.isReadOnly) {
    return QuoteReadOnlyPlugin(options);
  }
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;
      switch (element.type) {
        case ELEMENT_QUOTE: {
          return <QuoteElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_QUOTE_CONTENT: {
          return <QuoteElementContentEditable {...renderProps} {...options} />;
        }
        case ELEMENT_QUOTE_AUTHOR: {
          return <QuoteElementAuthorEditable {...renderProps} {...options} />;
        }
      }
    },
  };
};

export const withQuotePluginEditorChain =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { deleteBackward, deleteForward, insertBreak, normalizeNode } = editor;

    const matchQuoteNode = (node) =>
      [ELEMENT_QUOTE_CONTENT, ELEMENT_QUOTE_AUTHOR, ELEMENT_QUOTE].includes(node?.type as any);

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      if (node?.type === ELEMENT_QUOTE) {
        // Ensure CONTENT and AUTHOR is exist
        if (node.children.length > 2) {
          Transforms.removeNodes(editor, { at: [...path, 2] });
          return;
        }

        const firstNode = node.children[0];

        if (firstNode?.type !== ELEMENT_QUOTE_CONTENT) {
          if (firstNode) {
            Transforms.removeNodes(editor, { at: [...path, 0] });
          }
          Transforms.insertNodes(
            editor,
            QuoteElementTransformActions.generateSingleQuoteContentNode(),
            { at: [...path, 0] }
          );
          return;
        }

        const secondNode = node.children[1];
        if (secondNode?.type !== ELEMENT_QUOTE_AUTHOR) {
          if (secondNode) {
            Transforms.removeNodes(editor, { at: [...path, 1] });
          }
          Transforms.insertNodes(
            editor,
            QuoteElementTransformActions.generateSingleQuoteAuthorNode(),
            { at: [...path, 1] }
          );
          return;
        }
      }
      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'backward',
        match: matchQuoteNode,
      });
      if (!canDelete) {
        return;
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'forward',
        match: matchQuoteNode,
      });
      if (!canDelete) {
        return;
      }
      deleteForward(unit);
    };

    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [table] = Editor.nodes(editor, {
          match: (n: any) => !Editor.isEditor(n) && Element.isElement(n) && matchQuoteNode(n),
        });

        if (table) {
          editor.insertText('\n');
          return;
        }
      }

      insertBreak();
    };

    return editor;
  };
